import { useAuth } from "../contexts/useAuth";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "../contexts/TranslationContext";
import { getHomePathForRole } from "../utils/roleHome";

function DownloadPage() {
  const { isAdmin, isPaid, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState({ windows: false, mac: false });
  const [available, setAvailable] = useState({ windows: false, mac: false });
  const autoTriggered = useRef(false);

  const handleDownload = useCallback(async (os) => {
    try {
      setLoading(prev => ({ ...prev, [os]: true }));
      const response = await fetch(`${import.meta.env.VITE_API_URL}/liberar-download?os=${os}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.downloadLink) {
          window.location.href = data.downloadLink;
          toast.success(t("download.started"));
        }
      } else {
        const data = await response.json().catch(() => ({}));
        if (data.error === "payment_required") {
          toast.error(t("download.paymentPending"));
          navigate("/buy");
        } else {
          toast.error(t("download.error"));
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(t("download.connectionError"));
    } finally {
      setLoading(prev => ({ ...prev, [os]: false }));
    }
  }, [token, t, navigate]);

  useEffect(() => {
    if (!token || !isPaid) {
      toast.error(t("download.accessDenied"));
      navigate(getHomePathForRole({ isAdmin, isPaid }));
      return;
    }

    fetch(`${import.meta.env.VITE_API_URL}/api/desktop/latest-version`)
      .then(r => r.json())
      .then(data => {
        const avail = {
          windows: !!data.windows?.url,
          mac: !!data.mac?.url,
        };
        setAvailable(avail);

        const osParam = searchParams.get('os');
        if (osParam && !autoTriggered.current && (osParam === 'windows' || osParam === 'mac') && avail[osParam]) {
          autoTriggered.current = true;
          handleDownload(osParam);
        }
      })
      .catch(() => {});
  }, [token, isPaid, navigate, searchParams, t, handleDownload]);

  return (
    <div className="flex-1 flex items-center justify-center px-4">
      <div className="bg-[#111] border border-white/10 p-8 rounded-xl shadow-lg text-center max-w-md w-full">
        <h1 className="text-2xl font-bold text-white mb-4">
          {t("download.title")}
        </h1>
        <p className="text-gray-300 mb-6">
          {t("download.thankYou")}
        </p>
        <div className="flex flex-col gap-3">
          {available.windows && (
            <button
              onClick={() => handleDownload('windows')}
              disabled={loading.windows}
              className="w-full bg-brand-500 hover:bg-brand-400 text-black font-bold py-3 px-4 rounded-md transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
              </svg>
              {loading.windows ? t("download.downloading") || "Baixando..." : "Download Windows"}
            </button>
          )}
          {available.mac && (
            <button
              onClick={() => handleDownload('mac')}
              disabled={loading.mac}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-4 rounded-md transition border border-white/20 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              {loading.mac ? t("download.downloading") || "Baixando..." : "Download macOS"}
            </button>
          )}
          {!available.windows && !available.mac && (
            <p className="text-gray-500 text-sm">
              {t("download.noVersions") || "Nenhuma versao disponivel no momento."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default DownloadPage;
